import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useWindowDimensions } from "react-native";
import { Adapt, isWeb, Popover, ScrollView, Sheet, type TamaguiElement, YStack } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { spaceTokens } from "../../themes/token-space";
import { useSafeAreaInsets } from "../../utils";
import { FIELD_FRAME_ATTR } from "../FieldShell/constants";
import { SHEET_GRABBER_HEIGHT, SHEET_GRABBER_WIDTH } from "../sheetStyle";
import { getReturnFocusTarget } from "./focusReturn";
import type { DropdownProps } from "./types";
import { useInSheet } from "./useInSheet";
const MIN_WIDTH = 200;
const PLACEMENT = "bottom-start";
const ANCHOR_GAP = 4;
// Leaves a peek above the sheet so a long list scrolls internally, not off-screen.
const SHEET_SCREEN_TOP_GAP = spaceTokens.sp10;
// Fixed-height (`sheetFullHeight`) sheet detent, as a % of screen height.
const FULL_SHEET_PERCENT = 90;

// Adaptive floating surface: an anchored `Popover` on web (including iOS
// Safari), the same content teleported into a bottom `Sheet` on native via
// `Adapt`. The popover-only props (`matchTriggerWidth`, `maxHeight`) are
// inert in the sheet. Height is unopinionated: content fills to the viewport
// edge unless a consumer caps it via `maxHeight` (e.g. the option-list usage).
export const Dropdown = ({
  open,
  onOpenChange,
  trigger,
  anchor,
  returnFocusRef,
  children,
  sheetHeader,
  sheetFullHeight = false,
  matchTriggerWidth = true,
  maxHeight,
  "aria-activedescendant": activeDescendant,
  contentLabel
}: DropdownProps) => {
  // Wraps the rendered trigger/anchor; used to measure the field frame and (trigger
  // mode) find the focusable to restore on close.
  const wrapperRef = useRef<TamaguiElement | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const [belowFrameOffset, setBelowFrameOffset] = useState(0);
  const insets = useSafeAreaInsets();
  const {
    height: windowHeight
  } = useWindowDimensions();
  const sheetScrollMaxHeight = windowHeight - insets.top - SHEET_SCREEN_TOP_GAP;
  const inSheet = useInSheet();
  const measureAnchor = useCallback(() => {
    // Popover anchoring is web-only; native renders the Adapt sheet instead.
    if (!isWeb) {
      return;
    }
    const node = wrapperRef.current;
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const field = node.firstElementChild;
    const widthAnchor = field instanceof HTMLElement ? field : node;
    if (matchTriggerWidth) {
      setTriggerWidth(widthAnchor.getBoundingClientRect().width);
    }
    // Anchor below the field frame, not the helper-text row. Frame depth varies by
    // field, so find it by FieldShell's attribute rather than depth or a CSS class.
    const frameEl = node.querySelector(`[${FIELD_FRAME_ATTR}]`);
    const frame = frameEl instanceof HTMLElement ? frameEl : widthAnchor;
    const belowFrame = node.getBoundingClientRect().bottom - frame.getBoundingClientRect().bottom;
    setBelowFrameOffset(Math.max(0, belowFrame));
  }, [matchTriggerWidth]);

  // Measure post-layout (width/anchor aren't known on first paint) and on each open.
  useLayoutEffect(() => {
    if (open) {
      measureAnchor();
    }
  }, [open, measureAnchor]);

  // Coalesce resize bursts to one measure per frame — each measure forces reflow.
  const handleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      return;
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      measureAnchor();
    });
  }, [measureAnchor]);
  useEffect(() => {
    if (!open || !isWeb) {
      return;
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [open, handleResize]);

  // jsdom lacks scrollIntoView, hence the optional call.
  useEffect(() => {
    if (!open || !isWeb || activeDescendant === undefined) {
      return;
    }
    document.getElementById(activeDescendant)?.scrollIntoView?.({
      block: "nearest"
    });
  }, [open, activeDescendant]);
  const handleOpenChange = useCallback((next: boolean) => {
    onOpenChange(next);
    // Trigger mode close-only: `Popover.Trigger` wraps a non-focusable YStack,
    // so restore focus to the caller's trigger. Anchor mode uses onCloseAutoFocus.
    if (!next && anchor === undefined && isWeb && wrapperRef.current instanceof HTMLElement) {
      getReturnFocusTarget(wrapperRef.current)?.focus();
    }
  }, [onOpenChange, anchor]);

  // Anchor mode: on close, focus the inner opener instead of letting Popover's
  // teardown chase the unmounting content down to <body>. Fires at focus-scope
  // teardown, so it wins the race without a timeout (not under jsdom — see below).
  const handleCloseAutoFocus = useCallback((event: Event) => {
    if (anchor === undefined) {
      return;
    }
    event.preventDefault();
    if (isWeb && returnFocusRef?.current instanceof HTMLElement) {
      returnFocusRef.current.focus();
    }
  }, [anchor, returnFocusRef]);

  // Anchor mode jsdom/programmatic-close fallback for `onCloseAutoFocus` above
  // (which never fires under jsdom, nor on a close that skips `onOpenChange`).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (wasOpen && !open && isWeb && returnFocusRef?.current instanceof HTMLElement) {
      returnFocusRef.current.focus();
    }
  }, [open, returnFocusRef]);
  const contentWidth = matchTriggerWidth && triggerWidth !== undefined ? triggerWidth : undefined;
  return <Popover open={open} onOpenChange={handleOpenChange} placement={PLACEMENT}
  // Anchored below the trigger, never flips above it; `resize` caps height to
  // the space below so a near-bottom field scrolls internally, not over the field.
  allowFlip={false} stayInFrame resize offset={{
    mainAxis: ANCHOR_GAP - belowFrameOffset
  }} data-sentry-element="Popover" data-sentry-component="Dropdown" data-sentry-source-file="Dropdown.tsx">
      {anchor !== undefined ? <Popover.Anchor asChild>
          <YStack ref={wrapperRef}>{anchor}</YStack>
        </Popover.Anchor> : <Popover.Trigger asChild>
          <YStack ref={wrapperRef}>{trigger}</YStack>
        </Popover.Trigger>}

      {inSheet ? <Adapt when="sm" platform="touch">
          <Sheet native={!sheetFullHeight} modal dismissOnSnapToBottom snapPointsMode={sheetFullHeight ? "percent" : "fit"} snapPoints={sheetFullHeight ? [FULL_SHEET_PERCENT] : undefined}>
            <Sheet.Frame flex={sheetFullHeight ? 1 : undefined} paddingHorizontal="$sp1" backgroundColor="$surface1" borderTopLeftRadius="$br3" borderTopRightRadius="$br3" pointerEvents="auto">
              <YStack alignSelf="center" width={SHEET_GRABBER_WIDTH} height={SHEET_GRABBER_HEIGHT} marginTop="$sp2" marginBottom="$sp1" borderRadius={SHEET_GRABBER_HEIGHT / 2} backgroundColor="$bgDisabledSubtle" />
              {sheetHeader ? <YStack paddingTop="$sp2">{sheetHeader}</YStack> : null}
              <Sheet.ScrollView flex={sheetFullHeight ? 1 : undefined} maxHeight={sheetFullHeight ? undefined : sheetScrollMaxHeight} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" contentContainerStyle={{
            paddingBottom: insets.bottom + spaceTokens.sp1
          }}>
                <Adapt.Contents />
              </Sheet.ScrollView>
            </Sheet.Frame>
            <Sheet.Overlay backgroundColor="$utilityOverlayDark" pointerEvents="auto" />
          </Sheet>
        </Adapt> : null}

      <Popover.Content onCloseAutoFocus={handleCloseAutoFocus} aria-label={contentLabel} pointerEvents="auto" backgroundColor="$surface2" borderColor="$borderDefault" borderWidth={1} borderRadius="$br2"
    // Popover owns the horizontal inset here; the inner ScrollView owns vertical.
    paddingHorizontal="$sp1" paddingVertical="$sp0" zIndex="$modal" width={contentWidth} minWidth={MIN_WIDTH} overflow="hidden" opacity={1} scale={1} y={0} enterStyle={{
      opacity: 0,
      scale: 0.97,
      y: -4
    }} exitStyle={{
      opacity: 0,
      scale: 0.97,
      y: -4
    }} transition="quick" {...shadow.medium} data-sentry-element="Popover.Content" data-sentry-source-file="Dropdown.tsx">
        <ScrollView
      // Fill + scroll within Popover.Content (whose height `resize` shrinks near
      // the edge); minHeight 0 lets the flex child shrink to scroll, not overflow.
      flex={inSheet ? undefined : 1} minHeight={inSheet ? undefined : 0}
      // Optional consumer cap goes on the scroller, not Popover.Content: Popover
      // `resize` overwrites Content's maxHeight with the available viewport height
      // (inline style), so a cap there is dead. Undefined = fill to the viewport
      // edge; `flex: 1` still lets it shrink below the cap when space is tight.
      maxHeight={inSheet ? undefined : maxHeight} scrollEnabled={!inSheet} keyboardShouldPersistTaps="handled" width="100%" contentContainerStyle={{
        width: "100%",
        paddingVertical: spaceTokens.sp1
      }} data-sentry-element="ScrollView" data-sentry-source-file="Dropdown.tsx">
          {children}
        </ScrollView>
      </Popover.Content>
    </Popover>;
};